/**
 * PlatformKPIs — enterprise command-center strip for the admin dashboard.
 * Revenue + fees, volume, revenue-by-day chart, top events / pages / guides.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, DollarSign, Percent, Ticket, Gift, ShoppingCart, TrendingUp, TrendingDown, BarChart3, Globe, BookOpen, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const usdShort = (c) => `$${(c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const KPI = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className="rounded-lg border border-border bg-background p-3.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
      <Icon className={`h-3.5 w-3.5 ${color}`} /> {label}
    </div>
    <p className="text-xl font-bold mt-1 leading-tight">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export default function PlatformKPIs({ token }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/platform-kpis?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, days]);

  useEffect(() => {
    axios.get(`${API}/admin/platform-financials`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setFin(res.data))
      .catch(() => {});
  }, [token]);

  if (loading && !data) return (
    <Card className="bg-card border-border"><CardContent className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></CardContent></Card>
  );
  if (!data) return null;

  const p = data.period;
  const growth = data.gross_growth_pct;
  const maxDay = Math.max(1, ...data.revenue_by_day.map(d => d.gross_cents));

  return (
    <Card className="bg-card border-border" data-testid="platform-kpis">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-400" /> Platform Performance
        </CardTitle>
        <div className="flex gap-1">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${days === d ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
              data-testid={`kpi-days-${d}`}>{d}d</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KPI icon={DollarSign} color="text-emerald-400" label="Gross" testid="kpi-gross"
            value={usd(p.gross_cents)}
            sub={growth != null ? (
              <span className={growth >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {growth >= 0 ? '▲' : '▼'} {Math.abs(growth)}% vs prev {days}d
              </span>
            ) : `all-time ${usdShort(data.all_time.gross_cents)}`} />
          <KPI icon={Percent} color="text-amber-400" label="Fees Earned" testid="kpi-fees"
            value={usd(p.fees_cents)} sub={`all-time ${usdShort(data.all_time.fees_cents)}`} />
          <KPI icon={Ticket} color="text-indigo-400" label="Tickets" testid="kpi-tickets"
            value={p.tickets.toLocaleString()} sub={`all-time ${data.all_time.tickets.toLocaleString()}`} />
          <KPI icon={Gift} color="text-fuchsia-400" label="Add-ons" testid="kpi-addons"
            value={p.addons.toLocaleString()} sub={`all-time ${data.all_time.addons.toLocaleString()}`} />
          <KPI icon={ShoppingCart} color="text-sky-400" label="Orders" testid="kpi-orders"
            value={p.orders.toLocaleString()}
            sub={p.orders > 0 ? `avg ${usd(Math.round(p.gross_cents / p.orders))}` : '—'} />
          <KPI icon={Globe} color="text-teal-400" label="Traffic" testid="kpi-traffic"
            value={data.traffic_total.toLocaleString()} sub={`page views · ${days}d`} />
        </div>

        {fin && (
          <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3.5" data-testid="kpi-financials-snapshot">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <DollarSign className="h-3 w-3 text-emerald-400" /> Eventism Financials
              </p>
              <button onClick={() => navigate('/admin/platform-financials')}
                className="text-[11px] font-semibold text-emerald-400 hover:underline" data-testid="kpi-financials-link">
                Open Platform Financials →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><p className="text-[10px] text-muted-foreground uppercase">Service fees (30d)</p>
                <p className="text-sm font-bold">{usd(fin.revenue?.last_30d?.fees_cents || 0)}</p>
                <p className="text-[10px] text-muted-foreground">all-time {usdShort(fin.revenue?.all_time?.fees_cents || 0)}</p></div>
              <div><p className="text-[10px] text-muted-foreground uppercase">Money out (30d)</p>
                <p className="text-sm font-bold">{usd(Object.values(fin.money_out?.last_30d || {}).reduce((a, v) => a + (v?.cents || 0), 0))}</p>
                <p className="text-[10px] text-muted-foreground">payouts · payments · credits</p></div>
              <div><p className="text-[10px] text-muted-foreground uppercase">Wallet liabilities</p>
                <p className="text-sm font-bold">{usd(fin.liabilities?.total_cents || 0)}</p>
                <p className="text-[10px] text-muted-foreground">{(fin.liabilities?.org_wallets_count || 0) + (fin.liabilities?.user_wallets_count || 0)} wallets</p></div>
              <div><p className="text-[10px] text-muted-foreground uppercase">Stripe balance</p>
                <p className="text-sm font-bold">{fin.stripe_balance ? usd(fin.stripe_balance.available_cents) : '—'}</p>
                <p className="text-[10px] text-muted-foreground">{fin.stripe_balance ? `+ ${usdShort(fin.stripe_balance.pending_cents)} pending` : 'not connected'}</p></div>
            </div>
          </div>
        )}

        {data.revenue_by_day.length > 1 && (
          <div data-testid="kpi-revenue-chart">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Revenue by day</p>
            <div className="flex items-end gap-[2px] h-16">
              {data.revenue_by_day.map(d => (
                <div key={d.date} className="flex-1 bg-indigo-500/70 hover:bg-indigo-400 rounded-t transition-colors min-w-[3px]"
                  style={{ height: `${Math.max(4, Math.round(100 * d.gross_cents / maxDay))}%` }}
                  title={`${d.date}: ${usd(d.gross_cents)} (${d.orders} orders)`} />
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          <div data-testid="kpi-top-events">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-emerald-400" /> Top Events ({days}d)</p>
            <div className="space-y-1.5">
              {data.top_events.length === 0 && <p className="text-xs text-muted-foreground">No sales in this period.</p>}
              {data.top_events.map(e => (
                <button key={e.event_id} onClick={() => navigate(`/admin/events/${e.event_id}/transactions`)}
                  className="w-full flex items-center justify-between gap-2 text-left rounded-md px-2 py-1.5 hover:bg-accent transition-colors">
                  <span className="text-xs font-medium truncate">{e.name}</span>
                  <span className="text-xs font-bold text-emerald-400 whitespace-nowrap">{usdShort(e.gross_cents)} · {e.tickets}t</span>
                </button>
              ))}
            </div>
          </div>
          <div data-testid="kpi-top-pages">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><Globe className="h-3 w-3 text-teal-400" /> Top Pages ({days}d)</p>
            <div className="space-y-1.5">
              {data.top_pages.length === 0 && <p className="text-xs text-muted-foreground">No traffic recorded.</p>}
              {data.top_pages.map(pg => (
                <div key={pg.path} className="flex items-center justify-between gap-2 px-2 py-1">
                  <span className="text-xs font-mono truncate text-muted-foreground">{pg.path}</span>
                  <span className="text-xs font-bold whitespace-nowrap">{pg.views.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="kpi-top-guides">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><BookOpen className="h-3 w-3 text-rose-400" /> Top Editorial Guides</p>
            <div className="space-y-1.5">
              {data.top_guides.length === 0 && <p className="text-xs text-muted-foreground">No published guides.</p>}
              {data.top_guides.map(g => (
                <div key={g.slug} className="flex items-center justify-between gap-2 px-2 py-1">
                  <span className="text-xs font-medium truncate">{g.title}</span>
                  <span className="text-xs font-bold whitespace-nowrap">{(g.view_count || 0).toLocaleString()} views</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
          {[
            { label: 'Upcoming events', value: data.counts.upcoming_events, icon: CalendarDays },
            { label: 'Organizers', value: data.counts.organizations },
            { label: 'Venues', value: data.counts.venues },
            { label: 'Managed clients', value: data.counts.managed_clients },
            { label: 'Published guides', value: data.counts.published_guides },
            { label: 'Users', value: data.counts.users },
          ].map(c => (
            <Badge key={c.label} variant="outline" className="text-[10px] gap-1.5 py-1" data-testid={`kpi-count-${c.label.replace(/ /g, '-').toLowerCase()}`}>
              <span className="font-bold text-foreground">{(c.value || 0).toLocaleString()}</span> {c.label}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
